/**
 * Cliente da alerts-api com normalização e tratamento de erros
 */

import { USER_API_AUTHORIZATION } from "shared/auth/auth";
import { captureSentryException } from "shared/services/sentry/captureException";
import type {
  Alert,
  AlertsPage,
  CreateAlertPayload,
  EditAlertPayload,
  ToggleAlertPayload,
} from "./alerts.types";
import { normalizeAlert } from "shared/utils/alerts/alertFormat";

/**
 * Erro customizado para a alerts-api.
 */
export class AlertsApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "AlertsApiError";
    this.status = status;
  }
}

/**
 * Determina a URL base da alerts-api conforme o ambiente.
 * Espelha a lógica de getUserApiUrl em auth.ts.
 */
function getAlertsApiUrl(): string {
  const hostname = window.location.hostname.toLowerCase();

  if (hostname.includes("staging")) {
    return "https://alerts-api-staging.voopter.com.br";
  }

  if (hostname === "localhost") {
    return (
      process.env.REACT_APP_ALERTS_API_URL ??
      "https://alerts-api-staging.voopter.com.br"
    );
  }

  return (
    process.env.REACT_APP_ALERTS_API_URL ?? "https://alerts-api.voopter.com.br"
  );
}

/**
 * Faz requisição autenticada à alerts-api.
 * Trata erros de rede e respostas não-ok.
 */
async function alertsRequest(
  path: string,
  init?: RequestInit,
): Promise<Record<string, unknown> | null> {
  const url = `${getAlertsApiUrl()}${path}`;
  const headers: Record<string, string> = {
    Accept: "application/json",
    Authorization:
      process.env.REACT_APP_ALERTS_API_AUTHORIZATION ?? USER_API_AUTHORIZATION,
  };

  if (init?.body) {
    headers["Content-Type"] = "application/json";
  }

  if (init?.headers) {
    Object.assign(headers, init.headers);
  }

  try {
    const response = await fetch(url, {
      ...init,
      credentials: "include",
      headers,
    });

    if (!response.ok) {
      let message: string;
      try {
        const json = await response.json();
        message =
          typeof json.message === "string" && json.message.trim().length > 0
            ? json.message
            : "Tente novamente em instantes.";
      } catch {
        message = "Tente novamente em instantes.";
      }

      // API retorna 500 para alerta inexistente; normaliza para 404
      if (response.status === 500 && /not found|Invalid UUID/i.test(message)) {
        throw new AlertsApiError("Alerta não encontrado.", 404);
      }

      throw new AlertsApiError(message, response.status);
    }

    if (response.status === 204) {
      return null;
    }

    const text = await response.text();
    return text ? JSON.parse(text) : null;
  } catch (error) {
    if (error instanceof AlertsApiError) {
      throw error;
    }

    if (error instanceof TypeError || error instanceof SyntaxError) {
      throw new AlertsApiError(
        "Não foi possível conectar. Tente novamente em instantes.",
        0,
      );
    }

    throw error;
  }
}

/**
 * Obtém a lista paginada de alertas do usuário.
 */
export async function getAlerts(
  userId: string,
  page: number = 1,
  limit: number = 10,
): Promise<AlertsPage> {
  try {
    const params = new URLSearchParams({
      "user-id": userId,
      page: String(page),
      limit: String(limit),
    });

    const json = await alertsRequest(`/user/get-alerts?${params.toString()}`);

    const alerts = (Array.isArray(json?.data) ? json.data : []).map(
      (item) => normalizeAlert(item) as Alert,
    );

    const hasMore = alerts.length === limit;

    return { alerts, hasMore };
  } catch (error) {
    if (
      error instanceof AlertsApiError &&
      error.status !== 401 &&
      error.status !== 404
    ) {
      captureSentryException(error, {
        tags: { feature: "alerts", operation: "list" },
        contexts: { alerts: { status: error.status } },
      });
    }

    throw error;
  }
}

/**
 * Obtém um alerta específico pelo ID.
 */
export async function getAlert(id: string): Promise<Alert> {
  try {
    const json = await alertsRequest(
      `/user/get-alert/${encodeURIComponent(id)}`,
    );

    if (!json?.data) {
      throw new AlertsApiError("Alerta não encontrado.", 404);
    }

    return normalizeAlert(json.data) as Alert;
  } catch (error) {
    if (
      error instanceof AlertsApiError &&
      error.status !== 401 &&
      error.status !== 404
    ) {
      captureSentryException(error, {
        tags: { feature: "alerts", operation: "get" },
        contexts: { alerts: { status: error.status } },
      });
    }

    throw error;
  }
}

/**
 * Cria um novo alerta.
 */
export async function createAlert(payload: CreateAlertPayload): Promise<Alert> {
  try {
    const json = await alertsRequest("/user/create-alert", {
      method: "POST",
      body: JSON.stringify(payload),
    });

    return normalizeAlert(json?.data ?? {}) as Alert;
  } catch (error) {
    if (
      error instanceof AlertsApiError &&
      error.status !== 401 &&
      error.status !== 404
    ) {
      captureSentryException(error, {
        tags: { feature: "alerts", operation: "create" },
        contexts: { alerts: { status: error.status } },
      });
    }

    throw error;
  }
}

/**
 * Edita um alerta existente.
 */
export async function editAlert(payload: EditAlertPayload): Promise<Alert> {
  try {
    const json = await alertsRequest("/user/edit-alert", {
      method: "PUT",
      body: JSON.stringify(payload),
    });

    return normalizeAlert(json?.data ?? {}) as Alert;
  } catch (error) {
    if (
      error instanceof AlertsApiError &&
      error.status !== 401 &&
      error.status !== 404
    ) {
      captureSentryException(error, {
        tags: { feature: "alerts", operation: "edit" },
        contexts: { alerts: { status: error.status } },
      });
    }

    throw error;
  }
}

/**
 * Ativa ou desativa um alerta.
 */
export async function toggleAlertStatus(
  payload: ToggleAlertPayload,
): Promise<Alert> {
  try {
    const json = await alertsRequest("/user/edit-alert", {
      method: "PUT",
      body: JSON.stringify(payload),
    });

    return normalizeAlert(json?.data ?? {}) as Alert;
  } catch (error) {
    if (
      error instanceof AlertsApiError &&
      error.status !== 401 &&
      error.status !== 404
    ) {
      captureSentryException(error, {
        tags: { feature: "alerts", operation: "toggle" },
        contexts: { alerts: { status: error.status } },
      });
    }

    throw error;
  }
}

/**
 * Apaga um alerta.
 */
export async function deleteAlert(id: string): Promise<void> {
  try {
    await alertsRequest(`/user/delete-alert/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  } catch (error) {
    if (
      error instanceof AlertsApiError &&
      error.status !== 401 &&
      error.status !== 404
    ) {
      captureSentryException(error, {
        tags: { feature: "alerts", operation: "delete" },
        contexts: { alerts: { status: error.status } },
      });
    }

    throw error;
  }
}
